// src/components/tuck_shop/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  FiBarChart2,
  FiUserCheck,
  FiTable,
  FiBook,
  FiGrid,
  FiPackage,
  FiPieChart,
  FiHelpCircle,
  FiMenu,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import { FaUtensils } from 'react-icons/fa';
import logo from '../../assets/logo.jpg';
import { ACCE_BRAND } from '../../theme/colors';

// ════════════════════════════════════════════
// BRAND PALETTE (sourced from ACCE_BRAND)
// ════════════════════════════════════════════
const BRAND = {
  // Core (navy primary + orange accent, from ACCE logo)
  primary:       ACCE_BRAND.navy,          // #2A3A54
  primaryDark:   ACCE_BRAND.navyDark,      // #1E2A3E
  primaryLight:  ACCE_BRAND.navyLight,     // #3D5273
  accent:        ACCE_BRAND.orange,        // #F3811E
  accentDark:    ACCE_BRAND.orangeDark,    // #D96A0A
  accentLight:   ACCE_BRAND.orangeLight,   // #FFA04D

  // Tinted overlays
  primarySoft:   'rgba(42, 58, 84, 0.10)',
  primaryGlow:   'rgba(42, 58, 84, 0.28)',
  accentSoft:    'rgba(243, 129, 30, 0.12)',
  accentGlow:    'rgba(243, 129, 30, 0.30)',

  // Text
  primaryText:   ACCE_BRAND.navy,          // active sub-item text
  accentText:    ACCE_BRAND.orangeDark,    // active sub-item icon

  // Surfaces
  surface:       ACCE_BRAND.white,
  surfaceAlt:    ACCE_BRAND.offWhite,

  // Gradients
  gradient:      `linear-gradient(135deg, ${ACCE_BRAND.navyLight} 0%, ${ACCE_BRAND.navy} 50%, ${ACCE_BRAND.navyDark} 100%)`,
  gradientHover: `linear-gradient(135deg, ${ACCE_BRAND.navyLight} 0%, ${ACCE_BRAND.navy} 40%, ${ACCE_BRAND.navyDark} 100%)`,

  // Structure
  textPrimary:   ACCE_BRAND.darkText,
  textMuted:     ACCE_BRAND.mutedText,
  borderSoft:    'rgba(0, 0, 0, 0.06)',
  drawerWidth:   264,
};

const Sidebar = ({ activeMenu, setActiveMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    menu: false,
  });

  const currentPath = location.pathname;

  useEffect(() => {
    const path = currentPath.toLowerCase();
    const next = { ...expandedMenus };
    if (path.includes('/menu') || path.includes('/categories') || path.includes('/items')) {
      next.menu = true;
    }
    setExpandedMenus(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleNavigation = (menuName, path) => {
    if (setActiveMenu) setActiveMenu(menuName);
    if (path && path !== '#') navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  const isActive = (path, exact = false) => {
    if (!path || path === '#') return false;
    return exact ? currentPath === path : currentPath.startsWith(path) || currentPath === path;
  };

  const isSubmenuActive = (subItems) =>
    Array.isArray(subItems) && subItems.some((item) => isActive(item.path));

  const menuItems = [
    { name: 'Dashboard',        icon: <FiBarChart2 />, path: '/Sch/ICT/Dashboard_ict',   exact: true },
    { name: 'User Management',  icon: <FiUserCheck />, path: '/Sch/ICT/UserManagement',  exact: true },
    { name: 'Close Term',       icon: <FiPackage />,   path: '#' },
    { name: 'Portals',          icon: <FiPieChart />,  path: '#' },
  ];

  // ════════════════════════════════════════════
  // Shared styles
  // ════════════════════════════════════════════

  const sectionLabelSx = {
    display: 'block',
    px: 2,
    pt: 2,
    pb: 0.75,
    color: BRAND.textMuted,
    fontWeight: 800,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontSize: '0.62rem',
  };

  // ════════════════════════════════════════════
  // ACTIVE — navy gradient fill, orange accent rail + indicator dot
  // ════════════════════════════════════════════
  const activeButtonSx = {
    position: 'relative',
    borderRadius: '12px',
    mb: 0.5,
    minHeight: 48,
    pl: 2.2,
    pr: 1.5,
    color: '#fff',
    background: BRAND.gradient,
    boxShadow: `0 10px 26px -8px ${BRAND.primaryGlow}, 0 4px 12px -4px ${BRAND.primaryGlow}, inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(0,0,0,0.18)`,
    backdropFilter: 'blur(10px)',
    transition: 'all .25s cubic-bezier(.4,0,.2,1)',
    overflow: 'hidden',

    // Left accent rail — orange (ACCE accent)
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      left: 4,
      width: 3,
      borderRadius: 3,
      background: ACCE_BRAND.orange,
      boxShadow: `0 0 10px ${BRAND.accentGlow}`,
      pointerEvents: 'none',
    },

    // Soft top light
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '50%',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.20), transparent)',
      pointerEvents: 'none',
    },

    '&:hover': {
      background: BRAND.gradientHover,
      transform: 'translateX(2px)',
      boxShadow: `0 14px 30px -8px ${BRAND.primaryGlow}, inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(0,0,0,0.2)`,
    },

    '& .MuiListItemIcon-root': { color: '#fff' },
    '& .MuiListItemText-primary': {
      color: '#fff',
      fontWeight: 800,
      letterSpacing: '0.15px',
    },
  };

  // ════════════════════════════════════════════
  // IDLE — quiet, muted, so active pops
  // ════════════════════════════════════════════
  const idleButtonSx = {
    borderRadius: '12px',
    mb: 0.5,
    minHeight: 46,
    pl: 2.2,
    pr: 1.5,
    color: BRAND.textPrimary,
    transition: 'all .2s ease',
    '&:hover': {
      backgroundColor: BRAND.surfaceAlt,
      transform: 'translateX(2px)',
    },
    '& .MuiListItemIcon-root': { color: BRAND.textMuted },
    '& .MuiListItemText-primary': { color: BRAND.textPrimary, fontWeight: 600 },
  };

  // ════════════════════════════════════════════
  // SUB-ITEM ACTIVE — orange-tinted glass with accent rail
  // ════════════════════════════════════════════
  const activeSubButtonSx = {
    position: 'relative',
    borderRadius: '10px',
    mb: 0.4,
    minHeight: 40,
    pl: 5,
    pr: 1.5,
    color: BRAND.primaryText,
    background: `linear-gradient(135deg, ${BRAND.accentSoft} 0%, ${BRAND.primarySoft} 100%)`,
    border: `1px solid ${BRAND.accentSoft}`,
    backdropFilter: 'blur(8px)',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 12px -6px ${BRAND.accentGlow}`,
    transition: 'all .2s ease',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      left: 12,
      width: 3,
      borderRadius: 3,
      background: BRAND.accent,
      boxShadow: `0 0 8px ${BRAND.accentGlow}`,
      pointerEvents: 'none',
    },

    '&:hover': {
      background: `linear-gradient(135deg, rgba(243,129,30,0.20) 0%, rgba(42,58,84,0.12) 100%)`,
      transform: 'translateX(2px)',
    },

    '& .MuiListItemIcon-root': { color: BRAND.accentText },
    '& .MuiListItemText-primary': { color: BRAND.primaryText, fontWeight: 800 },
  };

  const idleSubButtonSx = {
    borderRadius: '10px',
    mb: 0.4,
    minHeight: 38,
    pl: 5,
    pr: 1.5,
    transition: 'all .2s ease',
    '&:hover': {
      backgroundColor: BRAND.surfaceAlt,
      transform: 'translateX(2px)',
    },
    '& .MuiListItemIcon-root': { color: BRAND.textMuted },
    '& .MuiListItemText-primary': { color: BRAND.textMuted, fontWeight: 500 },
  };

  // ════════════════════════════════════════════
  // Drawer content
  // ════════════════════════════════════════════
  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: BRAND.surface,
        overflow: 'hidden',
      }}
    >
      {/* ─── Brand Header (compact, logo pulled up) ─── */}
      <Box
        sx={{
          position: 'relative',
          pt: 1.5,
          pb: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderBottom: `1px solid ${BRAND.borderSoft}`,
          flexShrink: 0,
          background: `linear-gradient(180deg, ${BRAND.surfaceAlt} 0%, ${BRAND.surface} 100%)`,
          overflow: 'hidden',
        }}
      >
        {/* Soft brand glow behind logo — navy + orange blend */}
        <Box
          sx={{
            position: 'absolute',
            top: '-30%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${BRAND.accentSoft} 0%, ${BRAND.primarySoft} 45%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Logo — compact with subtle pulse ring */}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: -5,
              borderRadius: '50%',
              border: `2px solid ${BRAND.accentSoft}`,
              animation: 'pulseRing 3s ease-out infinite',
              pointerEvents: 'none',
            }}
          />
          <Box
            component="img"
            src={logo}
            alt="ACCE Logo"
            sx={{
              width: 68,
              height: 68,
              objectFit: 'cover',
              borderRadius: '50%',
              border: `2px solid ${BRAND.primarySoft}`,
              boxShadow: `0 8px 22px -8px ${BRAND.primaryGlow}, 0 3px 8px rgba(0,0,0,0.06)`,
              background: '#fff',
              p: 0.4,
              transition: 'transform .3s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
        </Box>
      </Box>

      {/* ─── Menu Scroll Area ─── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 1,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.12)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.22)',
          },
        }}
      >
        <Typography variant="caption" sx={sectionLabelSx}>
          Main
        </Typography>

        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            // Active if either the router path OR the activeMenu prop matches
            const pathActive = isActive(item.path, item.exact);
            const stateActive = activeMenu === item.name;
            const isItemActive = pathActive || stateActive;

            const isSubActive = item.hasSubmenu && isSubmenuActive(item.subItems);
            const isExpanded = expandedMenus[item.name.toLowerCase()] || isSubActive;
            const highlight = isItemActive || isSubActive;

            if (item.hasSubmenu) {
              return (
                <Box key={item.name}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => {
                        if (setActiveMenu) setActiveMenu(item.name);
                        toggleMenu(item.name.toLowerCase());
                      }}
                      sx={highlight ? activeButtonSx : idleButtonSx}
                    >
                      <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: highlight ? 800 : 600,
                          noWrap: true,
                        }}
                      />
                      {isExpanded
                        ? <FiChevronDown size={16} style={{ color: highlight ? '#fff' : BRAND.textMuted }} />
                        : <FiChevronRight size={16} style={{ color: highlight ? '#fff' : BRAND.textMuted }} />}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => {
                        const subActive = isActive(subItem.path, true);
                        return (
                          <ListItem key={subItem.name} disablePadding>
                            <ListItemButton
                              onClick={() => handleNavigation(subItem.name, subItem.path)}
                              sx={subActive ? activeSubButtonSx : idleSubButtonSx}
                            >
                              <ListItemIcon sx={{ minWidth: 32, fontSize: 16 }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.name}
                                primaryTypographyProps={{
                                  fontSize: 13,
                                  fontWeight: subActive ? 800 : 500,
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem key={item.name} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.name, item.path)}
                  disabled={item.path === '#'}
                  sx={highlight ? activeButtonSx : idleButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: highlight ? 800 : 600,
                      noWrap: true,
                    }}
                  />

                  {/* Active indicator dot — orange accent */}
                  {highlight && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: ACCE_BRAND.orange,
                        boxShadow: `0 0 10px ${BRAND.accentGlow}`,
                        ml: 0.5,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2, borderColor: BRAND.borderSoft }} />

        <Typography variant="caption" sx={{ ...sectionLabelSx, pt: 0 }}>
          Support
        </Typography>

        <List sx={{ py: 0 }}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton onClick={() => navigate('#')} sx={idleButtonSx}>
              <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
                <FiHelpCircle />
              </ListItemIcon>
              <ListItemText
                primary="Help"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 600, noWrap: true }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        {/* Footer */}
        <Box sx={{ px: 1.5, pt: 3, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: BRAND.textMuted,
              fontSize: '0.62rem',
              letterSpacing: 0.4,
              display: 'block',
              textAlign: 'center',
            }}
          >
            v2.4.0 · Tuck Shop
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Pulse animation for logo ring */}
      <style>{`
        @keyframes pulseRing {
          0%   { transform: scale(1);    opacity: 0.55; }
          100% { transform: scale(1.35); opacity: 0;    }
        }
      `}</style>

      {/* Mobile Hamburger */}
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          bgcolor: 'background.paper',
          boxShadow: 2,
          borderRadius: 2,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <FiMenu size={22} />
      </IconButton>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            borderRight: 'none',
            boxShadow: '0 0 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: BRAND.drawerWidth,
            borderRight: `1px solid ${BRAND.borderSoft}`,
            backgroundColor: BRAND.surface,
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;